import { useEffect, useRef, useState } from "react";
import {
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  query,
  startAfter,
} from "firebase/firestore";

const toRow = (snapshot, extra) => ({
  id: snapshot.id,
  ...snapshot.data(),
  ...extra,
});

// One page at a time of a Firestore query, with the current page kept live.
// `key` identifies the query (filters + sort): changing it starts again at
// page 1. `buildQuery()` returns the query without a limit.
export function usePagedQuery(key, buildQuery, { pageSize = 25, extra } = {}) {
  const build = useRef(buildQuery);
  build.current = buildQuery;
  const extraRef = useRef(extra);
  extraRef.current = extra;
  // Cursors of the pages visited so far (null = first page), per query key.
  const [pager, setPager] = useState({ key, cursors: [null] });
  const cursors = pager.key === key ? pager.cursors : [null];
  const cursor = cursors[cursors.length - 1];
  const [state, setState] = useState({
    rows: [],
    loading: !!key,
    error: null,
    hasNext: false,
    last: null,
  });

  useEffect(() => {
    if (!key) {
      setState({
        rows: [],
        loading: false,
        error: null,
        hasNext: false,
        last: null,
      });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    const base = build.current();
    const q = cursor
      ? query(base, startAfter(cursor), limit(pageSize + 1))
      : query(base, limit(pageSize + 1));
    return onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.slice(0, pageSize);
        setState({
          rows: docs.map((d) => toRow(d, extraRef.current)),
          loading: false,
          error: null,
          hasNext: snapshot.docs.length > pageSize,
          last: docs[docs.length - 1] || null,
        });
      },
      (error) => {
        console.error(`Paged query "${key}" failed`, error);
        setState({
          rows: [],
          loading: false,
          error,
          hasNext: false,
          last: null,
        });
      },
    );
  }, [key, cursor, pageSize]);

  return {
    ...state,
    page: cursors.length - 1,
    hasPrev: cursors.length > 1,
    next: () =>
      state.hasNext &&
      state.last &&
      setPager({ key, cursors: [...cursors, state.last] }),
    prev: () =>
      cursors.length > 1 && setPager({ key, cursors: cursors.slice(0, -1) }),
  };
}

// Server-side counts (Firestore aggregation queries: about one read per
// 1,000 matching documents). `defs` maps a name to a function returning a
// query; they run again whenever `key` changes.
export function useCounts(key, defs) {
  const defsRef = useRef(defs);
  defsRef.current = defs;
  const [state, setState] = useState({
    counts: {},
    loading: !!key,
    error: null,
  });

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    const entries = Object.entries(defsRef.current);
    Promise.all(entries.map(([, build]) => getCountFromServer(build())))
      .then((snapshots) => {
        if (cancelled) return;
        const counts = {};
        entries.forEach(([name], i) => {
          counts[name] = snapshots[i].data().count;
        });
        setState({ counts, loading: false, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(`Counts "${key}" failed`, error);
        setState({ counts: {}, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}

// Every document of a query, page by page (for CSV export).
export async function fetchAll(
  baseQuery,
  { extra, onProgress, pageSize = 500 } = {},
) {
  const rows = [];
  let cursor = null;
  for (;;) {
    const q = cursor
      ? query(baseQuery, startAfter(cursor), limit(pageSize))
      : query(baseQuery, limit(pageSize));
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((d) => rows.push(toRow(d, extra)));
    onProgress?.(rows.length);
    if (snapshot.docs.length < pageSize) return rows;
    cursor = snapshot.docs[snapshot.docs.length - 1];
  }
}

// Firestore's "needs an index" error carries a link that creates it.
export function describeQueryError(error) {
  if (!error) return null;
  if (error.code === "failed-precondition" && /index/i.test(error.message)) {
    return "This view needs a Firestore index that hasn't been created yet. Deploy firestore.indexes.json (see DEPLOYMENT.md) and wait for it to finish building.";
  }
  if (error.code === "permission-denied")
    return "You don't have access to this data.";
  return error.message || "Couldn't load data.";
}
